import React from 'react';
import { BoundTooltip, useClampedTooltip } from '../../components/ClampedTooltip';
import {
    ConsumeModeScore,
    ConsumePlayerRow,
    ConsumeRosterBand,
    ConsumeScoreMode,
    ConsumeScoreSlot,
    ConsumeSlot,
    ConsumeSlotBreakdown,
    ConsumeSlotResult,
    ConsumeSlotScores,
    ConsumeUseCount,
} from '../../types';

const CLASS_COLORS: Record<string, string> = {
    priest: '#ffffff',
    paladin: '#f58cba',
    warrior: '#c79c6e',
    hunter: '#abd473',
    rogue: '#fff569',
    shaman: '#0070de',
    mage: '#69ccf0',
    warlock: '#9482c9',
    druid: '#ff7d0a',
};

const SLOT_SCORE_LABELS: Record<ConsumeScoreSlot, string> = {
    battle: 'Battle',
    guardian: 'Guardian',
    food: 'Food',
    weapon: 'Weapon',
    potion: 'Potion',
    rune: 'Rune',
    sapper: 'Sapper',
    scrollAgi: 'Agility scroll',
    scrollStr: 'Strength scroll',
    scrollSpi: 'Spirit scroll',
};

const SCORE_SLOT_ORDER = ['battle', 'guardian', 'food', 'weapon', 'potion'] as const;

export const CONSUME_SCORE_MODES: Array<{ mode: ConsumeScoreMode; label: string; help: string }> = [
    {
        mode: 'relaxed',
        label: 'Relaxed',
        help: 'Battle elixir, guardian elixir (or flask), food, weapon, and potion. A valid but not best-for-spec consume keeps 75% credit. Battle elixir, food and weapon are full slots; the guardian elixir counts for half a slot (full for tanks) and potions for a quarter. Melee DPS (not hunters) are not scored on the guardian elixir.',
    },
    {
        mode: 'standard',
        label: 'Standard',
        help: 'Average of battle elixir, guardian elixir (or flask), food, weapon, and potion. Best-for-spec consume = full credit; other valid consume = half. Extra icons do not affect score. Potions are uses out of possible uses (2 min cooldown).',
    },
    {
        mode: 'sweaty',
        label: 'Very sweaty',
        help: 'Standard, plus every applicable extra as a full slot: agility/strength scrolls (melee), runes out of possible uses (healers, ret paladins, feral cats), sappers out of possible uses (melee DPS, not hunters), and spirit scrolls (priest and druid healers, only on pulls without Divine Spirit / Prayer of Spirit).',
    },
];

function scoreModeHelp(mode: ConsumeScoreMode): string {
    return CONSUME_SCORE_MODES.find((entry) => entry.mode === mode)?.help ?? '';
}

/** Score for the mode; reports cached before score modes existed only have the standard score. */
function playerModeScore(player: ConsumePlayerRow, mode: ConsumeScoreMode): ConsumeModeScore {
    const modeScore = player.scoreModes?.[mode];
    if (modeScore) {
        return modeScore;
    }
    return {
        score: player.score,
        parts: scoreBreakdown(player.slotScores).map((row) => ({ slot: row.slot, score: row.score, weight: 1 })),
    };
}

/** Keep the band grouping from the server, re-rank by the selected mode inside each band. */
function sortPlayersByMode(players: ConsumePlayerRow[], mode: ConsumeScoreMode): ConsumePlayerRow[] {
    const bandOrder = new Map<ConsumeRosterBand, number>();
    for (const player of players) {
        if (!bandOrder.has(player.band)) {
            bandOrder.set(player.band, bandOrder.size);
        }
    }
    return [...players].sort((left, right) => {
        const bandDiff = (bandOrder.get(left.band) ?? 0) - (bandOrder.get(right.band) ?? 0);
        if (bandDiff !== 0) {
            return bandDiff;
        }
        const scoreDiff = playerModeScore(right, mode).score - playerModeScore(left, mode).score;
        if (scoreDiff !== 0) {
            return scoreDiff;
        }
        return left.name.localeCompare(right.name);
    });
}

const ATTEND_HELP =
    'Boss attempts this player was in, out of the bosses in this view. Duplicate pulls across logs are counted once. Same presence as the consume columns.';

const ATTEND_BAR = '#718096';

const COST_HELP =
    "Cheaper of Dreamscythe Alliance TSM min buyout or Marks of the Illidari (1 mark = 1 flask, or 10 haste/destruction pots, billed per pot). Weapon oils are billed per application (5 charges per vial). Zone flasks and items with no auction-house price show as unpriced. Minus the player share of raw gold from boss kills (25-man: 250g per boss, 300g for the last; Gruul's Lair: 250g each; Magtheridon: 500g; Zul'Aman: 39g per boss, 46g for Daakara; Karazhan: 39g per boss), split across the roster present on each kill.";

const BOSS_GOLD_ICON_URL = 'https://wow.zamimg.com/images/wow/icons/medium/inv_misc_coin_02.jpg';

function formatCopper(copper: number): string {
    const gold = Math.round(copper / 10000);
    return `${gold.toLocaleString()}g`;
}

function gcd(left: number, right: number): number {
    let a = Math.abs(left);
    let b = Math.abs(right);
    while (b > 0) {
        const next = a % b;
        a = b;
        b = next;
    }
    return a || 1;
}

/** 2/5, 1, 1 2/5, 3/10 */
function formatFraction(numerator: number, denominator: number): string {
    if (denominator <= 0) {
        return String(numerator);
    }
    const whole = Math.floor(numerator / denominator);
    const remainder = numerator % denominator;
    if (remainder === 0) {
        return String(whole);
    }
    const divisor = gcd(remainder, denominator);
    const frac = `${remainder / divisor}/${denominator / divisor}`;
    if (whole === 0) {
        return frac;
    }
    return `${whole} ${frac}`;
}

function formatMarksUsed(marks: number): string {
    const tenths = Math.round(marks * 10);
    const text = formatFraction(tenths, 10);
    if (text === '1') {
        return '1 mark';
    }
    return `${text} marks`;
}

/** Share of multi-charge items used: oils come in vials, drums are one drum with many charges. */
function formatChargedItemShare(uses: number, chargesPerItem: number, kind: ConsumeUseCount['kind']): string {
    const unit = kind === 'drums' ? 'drum' : 'vial';
    const text = formatFraction(uses, chargesPerItem);
    if (text === '1') {
        return `1 ${unit}`;
    }
    if (text.includes('/')) {
        return `${text} ${unit}`;
    }
    return `${text} ${unit}s`;
}

function classColor(classLabel: string): string | undefined {
    const lower = classLabel.toLowerCase();
    const match = Object.keys(CLASS_COLORS).find((name) => lower.includes(name));
    return match ? CLASS_COLORS[match] : undefined;
}

function isExtraSlot(slot: ConsumeSlot): boolean {
    return (
        slot === 'rune' ||
        slot === 'sapper' ||
        slot === 'drums' ||
        slot === 'scroll-agi' ||
        slot === 'scroll-str' ||
        slot === 'scroll-spi' ||
        slot === 'kreeg'
    );
}

function showsUsesOnly(slot: ConsumeSlot): boolean {
    return slot === 'rune' || slot === 'sapper' || slot === 'drums' || slot === 'kreeg';
}

function slotTitle(slot: ConsumeSlotResult): string {
    if (showsUsesOnly(slot.slot)) {
        const uses = slot.potionUses ?? 0;
        const fights = (slot.totalCount ?? 0) > 1 ? ` · ${slot.appliedCount ?? 0}/${slot.totalCount} fights` : '';
        return `${slot.name}: ${uses} use${uses === 1 ? '' : 's'}${fights}`;
    }
    if (slot.slot === 'potion') {
        const uses = slot.potionUses ?? 0;
        const possible = slot.potionPossible ?? 0;
        const fights = (slot.totalCount ?? 0) > 1 ? ` · ${slot.appliedCount ?? 0}/${slot.totalCount} fights` : '';
        return `${slot.name}: ${uses}/${possible} uses (2 min CD)${fights}`;
    }
    const total = slot.totalCount ?? 0;
    if (total > 1) {
        return `${slot.name}: ${slot.appliedCount ?? 0}/${total} fights`;
    }
    return slot.present ? slot.name : `Missing ${slot.name}`;
}

function slotCountLabel(slot: ConsumeSlotResult): string | null {
    if (showsUsesOnly(slot.slot)) {
        return String(slot.potionUses ?? 0);
    }
    if (slot.slot === 'potion') {
        return `${slot.potionUses ?? 0}/${slot.potionPossible ?? 0}`;
    }
    const total = slot.totalCount ?? 0;
    if (total > 1) {
        return `${slot.appliedCount ?? 0}/${total}`;
    }
    return null;
}

function formatScorePct(score: number): string {
    const pct = Math.round(score * 1000) / 10;
    const text = Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
    return `${text}%`;
}

function scoreColor(score: number): string {
    const clamped = Math.max(0, Math.min(1, score));
    return `hsl(${clamped * 120}, 72%, 58%)`;
}

function scoreBreakdown(slotScores: ConsumeSlotScores): Array<{ slot: ConsumeScoreSlot; score: number }> {
    return SCORE_SLOT_ORDER.flatMap((slot) => {
        const value = slotScores[slot];
        if (value == null) {
            return [];
        }
        return [{ slot, score: value }];
    });
}

function scorePartLabel(slot: ConsumeScoreSlot, weight: number): string {
    const label = SLOT_SCORE_LABELS[slot];
    return weight === 1 ? label : `${label} (×${weight})`;
}

function shouldShowSlot(slot: ConsumeSlotResult, showExtras: boolean): boolean {
    if (!isExtraSlot(slot.slot)) {
        return true;
    }
    if (!showExtras || slot.applicable === false || !slot.present) {
        return false;
    }
    return true;
}

function slotStatusLabel(present: boolean, optimal: boolean | null | undefined): string | null {
    if (!present) {
        return 'Missing';
    }
    if (optimal === true) {
        return 'Optimal';
    }
    if (optimal === false) {
        return 'Not optimal';
    }
    return null;
}

function slotStatusClass(present: boolean, optimal: boolean | null | undefined): string {
    if (!present) {
        return 'is-missing';
    }
    if (optimal === true) {
        return 'is-optimal';
    }
    if (optimal === false) {
        return 'is-suboptimal';
    }
    return '';
}

function iconQualityClass(slot: ConsumeSlotResult): string {
    if (!slot.present) {
        return '';
    }
    if (slot.optimal === false) {
        return ' is-suboptimal';
    }
    if (slot.optimal === true) {
        return ' is-optimal';
    }
    return '';
}

function breakdownIconClass(row: ConsumeSlotBreakdown): string {
    if (!row.present) {
        return ' is-missing';
    }
    if (row.optimal === false) {
        return ' is-suboptimal';
    }
    if (row.optimal === true) {
        return ' is-optimal';
    }
    return '';
}

function isUsesSlot(slot: ConsumeSlot): boolean {
    return slot === 'potion' || showsUsesOnly(slot);
}

function tooltipBreakdowns(slot: ConsumeSlotResult): ConsumeSlotBreakdown[] {
    const parts = slot.breakdowns ?? [];
    const total = slot.totalCount ?? 0;
    if (total <= 1 && parts.length <= 1) {
        return [];
    }
    if (isUsesSlot(slot.slot)) {
        const used = parts.filter((part) => part.present);
        if (used.length > 0) {
            return used;
        }
    }
    return parts;
}

const ConsumeIcon: React.FC<{ slot: ConsumeSlotResult }> = ({ slot }) => {
    const tooltip = useClampedTooltip<HTMLSpanElement>('below');
    const count = slotCountLabel(slot);
    const extraClass = isExtraSlot(slot.slot) ? ' is-extra' : '';
    const rows = tooltipBreakdowns(slot);
    const simpleName = slot.breakdowns?.[0]?.name ?? slot.name;
    const simpleStatus = slotStatusLabel(slot.present, slot.optimal);
    return (
        <span
            ref={tooltip.anchorRef}
            className={`consume-icon${extraClass} ${slot.present ? 'is-present' : 'is-missing'}${iconQualityClass(slot)}`}
            tabIndex={0}
            aria-label={slotTitle(slot)}
            {...tooltip.anchorProps}
        >
            <img src={slot.iconUrl} alt="" />
            {count !== null && <span className="consume-icon-count">{count}</span>}
            <BoundTooltip open={tooltip.open} panelRef={tooltip.panelRef} className="consume-icon-panel">
                {rows.length > 0 ? (
                    <div className="consume-icon-panel-grid">
                        {rows.map((row) => {
                            const status = slotStatusLabel(row.present, row.optimal);
                            return (
                                <React.Fragment key={`${row.present ? '1' : '0'}:${row.consumeId}:${row.optimal}`}>
                                    <span className="consume-icon-panel-qty">×{row.count}</span>
                                    <img
                                        className={`consume-icon-panel-icon${breakdownIconClass(row)}`}
                                        src={row.iconUrl}
                                        alt=""
                                    />
                                    <span className="consume-icon-panel-name">{row.name}</span>
                                    {status ? (
                                        <span
                                            className={`consume-icon-panel-status ${slotStatusClass(
                                                row.present,
                                                row.optimal
                                            )}`}
                                        >
                                            {status}
                                        </span>
                                    ) : (
                                        <span />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                ) : (
                    <div className="consume-icon-panel-simple">
                        <span className="consume-icon-panel-name">{simpleName}</span>
                        {simpleStatus ? (
                            <span
                                className={`consume-icon-panel-status ${slotStatusClass(slot.present, slot.optimal)}`}
                            >
                                {simpleStatus}
                            </span>
                        ) : null}
                    </div>
                )}
            </BoundTooltip>
        </span>
    );
};

function costShareNote(entry: {
    kind: ConsumeUseCount['kind'];
    uses: number;
    chargesPerItem?: number;
    priceSource?: 'ah' | 'mark';
    marksUsed?: number;
}): string | null {
    const marks = entry.priceSource === 'mark' && entry.marksUsed ? entry.marksUsed : 0;
    if (marks > 0) {
        return formatMarksUsed(marks);
    }
    const charges = entry.chargesPerItem && entry.chargesPerItem > 1 ? entry.chargesPerItem : 0;
    if (charges > 0) {
        return formatChargedItemShare(entry.uses, charges, entry.kind);
    }
    return null;
}

const PlayerCell: React.FC<{ player: ConsumePlayerRow }> = ({ player }) => {
    const color = classColor(player.class);
    return (
        <div className="consume-player">
            <span className="consume-player-name" style={color ? { color } : undefined}>
                {player.name}
            </span>
            <span className="consume-player-spec">{player.class}</span>
        </div>
    );
};

const ScoreCell: React.FC<{ player: ConsumePlayerRow; mode: ConsumeScoreMode }> = ({ player, mode }) => {
    const tooltip = useClampedTooltip<HTMLDivElement>('right');
    const { score, parts } = playerModeScore(player, mode);
    const color = scoreColor(score);
    return (
        <div className="consume-score" style={{ color }} tabIndex={0} ref={tooltip.anchorRef} {...tooltip.anchorProps}>
            <span className="consume-score-value">{formatScorePct(score)}</span>
            <span className="consume-score-bar" aria-hidden="true">
                <span style={{ width: `${Math.max(0, Math.min(100, score * 100))}%`, background: color }} />
            </span>
            <BoundTooltip open={tooltip.open} panelRef={tooltip.panelRef} className="consume-score-panel">
                {parts.map((row) => (
                    <div key={row.slot} className="consume-score-panel-row">
                        <span>{scorePartLabel(row.slot, row.weight)}</span>
                        <span className="consume-score-panel-pct" style={{ color: scoreColor(row.score) }}>
                            {formatScorePct(row.score)}
                        </span>
                    </div>
                ))}
            </BoundTooltip>
        </div>
    );
};

const AttendanceCell: React.FC<{ player: ConsumePlayerRow }> = ({ player }) => {
    const tooltip = useClampedTooltip<HTMLDivElement>('right');
    const attended = player.bossAttended ?? 0;
    const total = player.bossAttempts ?? 0;
    const ratio = total > 0 ? attended / total : 0;
    const label = total > 0 ? `${attended}/${total}` : '—';
    return (
        <div className="consume-score consume-attend" tabIndex={0} ref={tooltip.anchorRef} {...tooltip.anchorProps}>
            <span className="consume-score-value">{label}</span>
            <span className="consume-score-bar" aria-hidden="true">
                <span style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%`, background: ATTEND_BAR }} />
            </span>
            <BoundTooltip open={tooltip.open} panelRef={tooltip.panelRef} className="consume-score-panel">
                <div className="consume-score-panel-row">
                    <span>Boss attempts</span>
                    <span className="consume-score-panel-pct" style={{ color: ATTEND_BAR }}>
                        {label}
                    </span>
                </div>
            </BoundTooltip>
        </div>
    );
};

/** Consume spend scaled up to a perfect score: a for-fun estimate, not a real price list. */
const CostCell: React.FC<{ player: ConsumePlayerRow; mode: ConsumeScoreMode; realmLabel?: string }> = ({
    player,
    mode,
    realmLabel,
}) => {
    const tooltip = useClampedTooltip<HTMLDivElement>('right');
    const perfectCost = player.optimalCost?.[mode];
    // The server prices every boss pull in the view; scale by attendance (all attempts treated as equal).
    const attendanceRatio =
        (player.bossAttempts ?? 0) > 0 ? (player.bossAttended ?? 0) / (player.bossAttempts ?? 1) : 1;
    const perfectCopper = perfectCost ? perfectCost.copper * attendanceRatio : 0;
    const modeLabel = CONSUME_SCORE_MODES.find((entry) => entry.mode === mode)?.label ?? mode;
    const copper = player.consumeCostCopper ?? 0;
    const unpriced = player.consumeUnpricedCount ?? 0;
    const goldCopper = player.bossGoldCopper ?? 0;
    const goldKills = player.bossGoldKills ?? 0;
    const netCopper = copper - goldCopper;
    const profit = netCopper < 0;
    const netLabel = profit ? `+${formatCopper(-netCopper)}` : formatCopper(netCopper);
    const label = copper > 0 || goldCopper > 0 ? netLabel : unpriced > 0 ? '—' : '0g';
    const uses = player.consumeUses ?? [];
    return (
        <div className="consume-cost" tabIndex={0} ref={tooltip.anchorRef} {...tooltip.anchorProps}>
            <span className={`consume-cost-value${profit ? ' is-profit' : ''}`}>{label}</span>
            <BoundTooltip open={tooltip.open} panelRef={tooltip.panelRef} className="consume-cost-panel">
                {uses.length === 0 && goldKills === 0 ? (
                    <div className="consume-cost-panel-empty">No counted consume uses</div>
                ) : (
                    <div className="consume-cost-panel-grid">
                        {uses.map((entry) => {
                            const priced = !entry.unpriced && entry.totalCopper != null;
                            const shareNote = costShareNote(entry);
                            return (
                                <React.Fragment key={entry.consumeId}>
                                    <span className="consume-cost-panel-qty">×{entry.uses}</span>
                                    {entry.iconUrl ? (
                                        <img className="consume-cost-panel-icon" src={entry.iconUrl} alt="" />
                                    ) : (
                                        <span className="consume-cost-panel-icon is-empty" aria-hidden="true" />
                                    )}
                                    <span className="consume-cost-panel-name">
                                        {entry.name}
                                        {shareNote ? (
                                            <span className="consume-cost-panel-mark">{shareNote}</span>
                                        ) : null}
                                    </span>
                                    <span className={`consume-cost-panel-price${priced ? '' : ' is-unpriced'}`}>
                                        {priced ? formatCopper(entry.totalCopper ?? 0) : 'unpriced'}
                                    </span>
                                </React.Fragment>
                            );
                        })}
                        {goldKills > 0 && uses.length > 0 ? (
                            <>
                                <span className="consume-cost-panel-total-label is-subtotal">Consumes subtotal</span>
                                <span className="consume-cost-panel-price is-total is-subtotal">
                                    {formatCopper(copper)}
                                </span>
                            </>
                        ) : null}
                        {goldKills > 0 ? (
                            <>
                                <span className="consume-cost-panel-qty">×{goldKills}</span>
                                <img className="consume-cost-panel-icon" src={BOSS_GOLD_ICON_URL} alt="" />
                                <span className="consume-cost-panel-name">
                                    Boss gold
                                    <span className="consume-cost-panel-mark">
                                        Split across the roster on each kill
                                    </span>
                                </span>
                                <span className="consume-cost-panel-price is-profit">+{formatCopper(goldCopper)}</span>
                            </>
                        ) : null}
                        <span className="consume-cost-panel-total-label">Total</span>
                        <span className={`consume-cost-panel-price is-total${profit ? ' is-profit' : ''}`}>
                            {netLabel}
                        </span>
                    </div>
                )}
                {unpriced > 0 ? (
                    <div className="consume-cost-panel-note">
                        {unpriced} unpriced item{unpriced === 1 ? '' : 's'}
                    </div>
                ) : null}
                {realmLabel ? <div className="consume-cost-panel-note">{realmLabel} auction house</div> : null}
                {perfectCost && perfectCopper > 0 ? (
                    <div
                        className="consume-cost-panel-note"
                        title="Optimal consumes for this spec on the pulls attended, at current prices"
                    >
                        Full {modeLabel} score: ~{formatCopper(perfectCopper)}
                        {perfectCost.unpricedSlots > 0 ? ` (${perfectCost.unpricedSlots} unpriced)` : ''}
                    </div>
                ) : null}
            </BoundTooltip>
        </div>
    );
};

interface ConsumesGridProps {
    columns: Array<{ key: string; label: string }>;
    players: ConsumePlayerRow[];
    tsmRealmLabel?: string;
    showExtras?: boolean;
    scoreMode?: ConsumeScoreMode;
}

const ConsumesGrid: React.FC<ConsumesGridProps> = ({
    columns,
    players: unsortedPlayers,
    tsmRealmLabel,
    showExtras = false,
    scoreMode = 'standard',
}) => {
    const players = React.useMemo(() => sortPlayersByMode(unsortedPlayers, scoreMode), [unsortedPlayers, scoreMode]);
    return (
        <div className="consume-grid-wrap">
            <table className="consume-grid">
                <thead>
                    <tr>
                        <th className="consume-player-col">Player</th>
                        <th className="consume-score-col" title={scoreModeHelp(scoreMode)}>
                            Score
                        </th>
                        <th className="consume-attend-col" title={ATTEND_HELP}>
                            Att.
                        </th>
                        <th className="consume-cost-col" title={COST_HELP}>
                            Cost
                        </th>
                        {columns.map((column) => (
                            <th key={column.key}>{column.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {players.map((player, index) => {
                        const previousBand: ConsumeRosterBand | undefined = players[index - 1]?.band;
                        const bandStart = index > 0 && previousBand !== player.band;
                        return (
                            <tr key={player.name} className={bandStart ? 'consume-band-start' : undefined}>
                                <th className="consume-player-col">
                                    <PlayerCell player={player} />
                                </th>
                                <td className="consume-score-col">
                                    <ScoreCell player={player} mode={scoreMode} />
                                </td>
                                <td className="consume-attend-col">
                                    <AttendanceCell player={player} />
                                </td>
                                <td className="consume-cost-col">
                                    <CostCell player={player} mode={scoreMode} realmLabel={tsmRealmLabel} />
                                </td>
                                {player.columns.map((column) => (
                                    <td
                                        key={column.columnKey}
                                        className={column.attended ? 'consume-slots-col' : 'consume-absent'}
                                    >
                                        {column.attended ? (
                                            <div className={`consume-slots${showExtras ? '' : ' hide-extras'}`}>
                                                {column.slots
                                                    .filter((slot) => shouldShowSlot(slot, showExtras))
                                                    .map((slot) => (
                                                        <ConsumeIcon key={slot.slot} slot={slot} />
                                                    ))}
                                            </div>
                                        ) : (
                                            <span className="consume-absent-label">—</span>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ConsumesGrid;
